import { useCallback, useRef, useState } from "react";
import type { PipelineStatus, VisualPayload } from "../types";
import { useAuthStore } from "../store/authStore";
import { useMaintenanceStore } from "../store/maintenanceStore";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

/** Stream callbacks for one /ask request. */
export interface AskCallbacks {
  onToken: (token: string, messageId: string) => void;
  onVisual: (visual: VisualPayload, messageId: string) => void;
  onDone: (messageId: string) => void;
  onError: (error: string) => void;
  /** Server-corrected transcript of a voice question. */
  onCorrection?: (correctedText: string) => void;
}

/** Everything one call to `ask()` needs. */
export interface AskRequestOptions {
  question: string;
  sessionId: string;
  messageId: string;
  callbacks: AskCallbacks;
  league?: string | null;
  voiceInput?: boolean;
  /** Phase B share URLs — when present, /ask pins season-intent
   * queries to this specific gbl_season row so the URL is stable
   * across rotations (e.g. /season/memories-in-motion/cups). */
  seasonSlug?: string | null;
  /** Speech-recogniser alternatives for a voice question (decisions/0006). */
  alternatives?: string[];
  /** Which recogniser heard a voice question: "browser" or the provider `/voice` named (decisions/0015). */
  voiceEngine?: string;
}

/**
 * useAsk — POST /ask and consume the dual-channel SSE stream.
 *
 * SSE channels:
 *   data: <token>             → appended to current assistant message
 *   event: visual / data: {} → parsed as VisualPayload, attached to message
 *
 * Returns:
 *   ask(options)             — trigger a question (see AskRequestOptions)
 *   isAsking                 — true while streaming
 */
export function useAsk() {
  const [isAsking, setIsAsking] = useState(false);
  const [status, setStatus] = useState<PipelineStatus>("idle");
  const abortRef = useRef<AbortController | null>(null);
  const { token, isAuthenticated, incrementGuest } = useAuthStore();

  const ask = useCallback(
    async ({
      question,
      sessionId,
      messageId,
      callbacks: { onToken, onVisual, onDone, onError, onCorrection },
      league,
      voiceInput,
      seasonSlug,
      alternatives,
      voiceEngine,
    }: AskRequestOptions): Promise<string> => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setIsAsking(true);
      setStatus("classifying");

      let hadError = false;
      try {
        // Build headers — attach JWT if authenticated (guest requests have no token)
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (isAuthenticated && token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const resp = await fetch(`${BASE_URL}/ask`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            question,
            session_id: sessionId,
            league: league ?? null,
            voice_input: voiceInput ?? false,
            season_slug: seasonSlug ?? null,
            alternatives: alternatives ?? [],
            voice_engine: voiceEngine ?? "browser",
          }),
          // Combine user-cancel with a hard timeout.
          // 45 s covers slow meta_question narrator runs (naming top-N Pokémon)
          // while keeping the UI responsive if the server hangs entirely.
          signal: AbortSignal.any
            ? AbortSignal.any([abortRef.current.signal, AbortSignal.timeout(45_000)])
            : abortRef.current.signal,
        });

        if (!resp.ok) {
          if (resp.status === 429) throw new Error("__rate_limit");
          if (resp.status === 422) throw new Error("__invalid_input");
          if (resp.status === 503) {
            useMaintenanceStore.getState().setMaintenance(true);
            throw new Error("__maintenance");
          }
          throw new Error("__server_error");
        }
        if (!resp.body) throw new Error("No response body");

        setStatus("narrating");

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let eventName = "message";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n").map((l) => l.replace(/\r$/, ""));
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (line.startsWith("event:")) {
              eventName = line.slice(6).trim();
            } else if (line.startsWith("data:")) {
              const data = line.startsWith("data: ") ? line.slice(6) : line.slice(5);
              if (eventName === "visual") {
                try {
                  const visual = JSON.parse(data) as VisualPayload;
                  onVisual(visual, messageId);
                } catch {
                  console.warn("Failed to parse visual payload");
                }
                eventName = "message";
              } else if (eventName === "correction") {
                if (data) onCorrection?.(data);
                eventName = "message";
              } else if (eventName === "error") {
                hadError = true;
                try {
                  const err = JSON.parse(data) as { message: string; hint?: string };
                  onError(err.hint ? `${err.message} ${err.hint}` : err.message);
                } catch {
                  onError("An unknown error occurred.");
                }
                setStatus("error");
                eventName = "message";
              } else if (data && data !== "[DONE]") {
                onToken(data, messageId);
              }
            } else if (line === "") {
              eventName = "message";
            }
          }
        }

        // Count guest questions after a successful ask
        if (!isAuthenticated) incrementGuest();

        onDone(messageId);
      } catch (err: unknown) {
        const errName = (err as Error)?.name;
        const errMsg = (err as Error)?.message ?? "";
        if (errName === "AbortError") {
          // Distinguish user-cancel (submit new question) from a timeout abort.
          // Some Android Chrome versions throw AbortError instead of TimeoutError
          // when AbortSignal.timeout fires via AbortSignal.any — check the cause.
          const isTimeoutAbort = (err as any)?.cause?.name === "TimeoutError";
          if (isTimeoutAbort) {
            hadError = true;
            onError("Scout took too long to respond. Please try again.");
            setStatus("error");
          } else {
            // Genuine user-cancel: mark done with empty content so the bubble
            // shows "No answer found" instead of staying stuck on "Generating…".
            onDone(messageId);
          }
        } else {
          hadError = true;
          let msg: string;
          if (errName === "TimeoutError") {
            msg = "Scout took too long to respond. Please try again.";
          } else if (errMsg === "__rate_limit") {
            msg = "You're sending messages too quickly — please wait a moment and try again.";
          } else if (errMsg === "__invalid_input") {
            msg = "That message couldn't be sent. Please rephrase your question and try again.";
          } else if (errMsg === "__maintenance") {
            msg = "Scout is taking a quick break to keep the lights on. We'll be back soon!";
          } else {
            msg = "Something went wrong. Please try again.";
          }
          onError(msg);
          setStatus("error");
        }
      } finally {
        setIsAsking(false);
        if (!hadError) setStatus("idle");
      }

      return messageId;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, isAuthenticated, incrementGuest],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { ask, cancel, isAsking, status, setStatus };
}
